import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Plus, Truck, X } from 'lucide-react';

import { extractApiMessage } from '@/lib/api';
import { fmtFecha, fmtInt } from '@/lib/format';
import { currentBundle } from '@/lib/offline/bundle';
import { useEntityId, useEntityMappings } from '@/lib/offline/operations';
import { useAuthStore } from '@/stores/auth';
import { useAnularSalida, useSalidasCamada } from '@/lib/queries/salidas';
import type { SalidaContexto } from '@/lib/types';
import { ModalOverlay } from './ModalOverlay';
import { ConfirmModal } from './ConfirmModal';

interface Props {
  camadaId: number;
  lotes: SalidaContexto['lotes'];
  cerrada: boolean;
  listadoCompleto?: boolean;
  ocultarCabecera?: boolean;
  onVerTodas?: () => void;
}

export function SalidasCamadaSection({
  camadaId,
  lotes,
  cerrada,
  listadoCompleto = false,
  ocultarCabecera = false,
  onVerTodas,
}: Props) {
  const user = useAuthStore((state) => state.user);
  const puedeRegistrar =
    user?.rol === 'admin' || user?.rol === 'ganadero' || user?.rol === 'operario';
  const puedeGestionar = user?.rol === 'admin' || user?.rol === 'ganadero';
  const query = useSalidasCamada(camadaId);
  const anular = useAnularSalida(camadaId);
  const [mostrarTodas, setMostrarTodas] = useState(false);
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [confirmandoId, setConfirmandoId] = useState<number | null>(null);
  const detalleActualId = useEntityId(detalleId, 'salidas');
  const confirmandoActualId = useEntityId(confirmandoId, 'salidas');
  const mappings = useEntityMappings();
  const canonicalId = (id: number) =>
    mappings.find((mapping) => mapping.entity === 'salidas' && mapping.localId === id)?.remoteId ??
    id;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(false),
    saving = useRef(false),
    epoch = useRef(0);
  const farm = currentBundle()?.explotacion_id ?? null;
  const context = `${user?.id ?? ''}:${camadaId}:${farm ?? ''}`;
  const viewed = useRef(context);
  viewed.current = context;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    const reset = () => {
      epoch.current++;
      saving.current = false;
      setBusy(false);
      setDetalleId(null);
      setConfirmandoId(null);
      setError(null);
      setMostrarTodas(false);
    };
    reset();
    window.addEventListener('offline:permissions-changed', reset);
    return () => window.removeEventListener('offline:permissions-changed', reset);
  }, [context]);

  const detalle = query.data?.find(
    (salida) => canonicalId(salida.id) === detalleActualId || salida.id === detalleId,
  );
  const path = `/camadas/${camadaId}/salidas`;
  const pesoLabel = (confirmed: boolean) =>
    confirmed ? 'Corregir peso destino' : 'Añadir peso destino';

  async function confirmarAnulacion() {
    const salida = query.data?.find(
      (item) => canonicalId(item.id) === confirmandoActualId || item.id === confirmandoId,
    );
    if (saving.current || !salida || salida.anulada || cerrada || !puedeGestionar) return;
    const owner = user?.id,
      farmBefore = currentBundle()?.explotacion_id ?? null;
    if (farmBefore != null && farmBefore !== salida.explotacion_id) {
      setError('La explotación ha cambiado. Vuelve a abrir la salida.');
      return;
    }
    const initialContext = context,
      attempt = epoch.current;
    const valid = () =>
      mounted.current &&
      epoch.current === attempt &&
      viewed.current === initialContext &&
      useAuthStore.getState().user?.id === owner &&
      (currentBundle()?.explotacion_id ?? null) === farmBefore;
    saving.current = true;
    setBusy(true);
    setError(null);
    try {
      await anular.mutateAsync(confirmandoActualId ?? salida.id);
      if (valid()) {
        setConfirmandoId(null);
        setDetalleId(null);
      }
    } catch (err) {
      if (valid()) setError(extractApiMessage(err, 'No se pudo anular la salida'));
    } finally {
      if (valid()) {
        saving.current = false;
        setBusy(false);
      }
    }
  }

  return (
    <section className="mb-4" data-testid="camada-bloque-salidas">
      {!ocultarCabecera && (
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-secondary">
          Salidas a matadero
        </h2>
      )}
      {!cerrada && puedeRegistrar && lotes.length > 0 && (
        <Link
          to={`${path}/nueva`}
          className="ui-primary mb-3 w-full"
          data-testid="salida-nueva-btn"
        >
          <Truck size={20} aria-hidden />
          Registrar salida
          <Plus size={18} aria-hidden />
        </Link>
      )}
      {query.isLoading && (
        <p role="status" className="text-sm text-text-secondary">
          Cargando salidas…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="parte-error">
          {extractApiMessage(query.error, 'No se pudieron cargar las salidas')}
        </p>
      )}
      {query.data?.length === 0 && <p className="cm-form-note">Aún no hay salidas registradas.</p>}
      {query.data && query.data.length > 0 && (
        <ul
          className="space-y-2"
          data-testid={listadoCompleto ? 'camada-listado-salidas' : 'camada-resumen-salidas'}
        >
          {(listadoCompleto || mostrarTodas ? query.data : query.data.slice(0, 3)).map((salida) => (
            <li
              key={salida.id}
              className="rounded-xl border border-border bg-surface p-3"
              data-testid={`salida-${salida.id}`}
            >
              <button
                type="button"
                className="flex min-h-11 w-full items-center gap-3 text-left"
                aria-label={`Consultar salida del ${fmtFecha(salida.fecha_carga)} · ${salida.matadero}`}
                onClick={() => {
                  setDetalleId(salida.id);
                  setError(null);
                }}
              >
                <span className="min-w-0 flex-1 break-words">
                  <strong className="block text-base">
                    {fmtFecha(salida.fecha_carga)} · {salida.matadero}
                  </strong>
                  <span className="mt-1 block text-sm text-text-secondary">
                    {salida.numero_camion ? `Camión ${salida.numero_camion}` : 'Sin camión'} ·{' '}
                    {salida.numero_albaran ? `Albarán ${salida.numero_albaran}` : 'Sin albarán'}
                  </span>
                  <span className="mt-1 block text-sm text-text-secondary">
                    {fmtInt(salida.unidades_cargadas_total)} aves ·{' '}
                    {salida.anulada
                      ? 'Anulada'
                      : salida.kg_confirmados_destino == null
                        ? 'Peso pendiente'
                        : `${fmtInt(Number(salida.kg_confirmados_destino))} kg`}
                  </span>
                </span>
                <ChevronRight size={20} className="shrink-0 text-text-secondary" aria-hidden />
              </button>
              {!cerrada && puedeRegistrar && !salida.anulada && (
                <Link
                  className="mt-2 inline-flex min-h-11 items-center text-sm font-medium text-accent"
                  to={`${path}/${salida.id}/peso`}
                >
                  {pesoLabel(salida.kg_confirmados_destino != null)}
                </Link>
              )}
            </li>
          ))}
          {!listadoCompleto && query.data.length > 3 && (
            <li>
              <button
                type="button"
                className="ui-secondary w-full"
                onClick={() => (onVerTodas ? onVerTodas() : setMostrarTodas((value) => !value))}
                data-testid="salidas-ver-todas"
              >
                {mostrarTodas ? 'Ver menos' : `Ver todas las salidas (${query.data.length})`}
              </button>
            </li>
          )}
        </ul>
      )}
      {detalle && confirmandoId == null && (
        <ModalOverlay
          placement="bottom"
          ariaLabelledby="salida-detalle-titulo"
          className="max-w-md"
          onBackdrop={() => setDetalleId(null)}
        >
          <header className="flex items-center justify-between gap-3">
            <h3 id="salida-detalle-titulo" className="ui-page-title">
              Salida del {fmtFecha(detalle.fecha_carga)}
            </h3>
            <button
              type="button"
              className="ui-icon-button"
              aria-label="Cerrar"
              onClick={() => setDetalleId(null)}
            >
              <X size={22} aria-hidden />
            </button>
          </header>
          {detalle.anulada && <p className="cm-form-note">Anulada</p>}
          <dl className="mt-4 grid grid-cols-2 gap-4 break-words text-base">
            <div>
              <dt className="text-sm text-text-secondary">Matadero</dt>
              <dd>{detalle.matadero}</dd>
            </div>
            <div>
              <dt className="text-sm text-text-secondary">Camión</dt>
              <dd>{detalle.numero_camion ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-sm text-text-secondary">Albarán</dt>
              <dd>{detalle.numero_albaran ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-sm text-text-secondary">Total</dt>
              <dd>{fmtInt(detalle.unidades_cargadas_total)} aves</dd>
            </div>
          </dl>
          <h4 className="mt-5 font-semibold">Animales por nave</h4>
          <ul className="mt-2 space-y-2 text-base">
            {detalle.lineas.map((linea) => (
              <li key={linea.id} className="flex min-h-11 items-center justify-between gap-3">
                <span>
                  Nave{' '}
                  {lotes.find((lote) => lote.lote_id === linea.lote_nave_id)?.nave_codigo ??
                    linea.lote_nave_id}
                  : {fmtInt(linea.unidades_cargadas)} aves
                </span>
              </li>
            ))}
          </ul>
          <dl className="mt-4 space-y-3 text-base">
            <div>
              <dt className="text-sm text-text-secondary">Peso destino</dt>
              <dd>
                {detalle.kg_confirmados_destino == null
                  ? 'Pendiente'
                  : `${fmtInt(Number(detalle.kg_confirmados_destino))} kg`}
              </dd>
            </div>
            {detalle.peso_medio_kg != null && (
              <div>
                <dt className="text-sm text-text-secondary">Peso medio</dt>
                <dd>
                  {detalle.peso_medio_kg.toLocaleString('es-ES', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{' '}
                  kg/ave
                </dd>
              </div>
            )}
            {detalle.fecha_recepcion_peso && (
              <div>
                <dt className="text-sm text-text-secondary">Fecha de recepción del peso</dt>
                <dd>{fmtFecha(detalle.fecha_recepcion_peso)}</dd>
              </div>
            )}
          </dl>
          {detalle.observaciones && (
            <div className="mt-4">
              <h4 className="text-sm text-text-secondary">Observaciones</h4>
              <p className="mt-1 whitespace-pre-wrap break-words">{detalle.observaciones}</p>
            </div>
          )}
          <div className="mt-5 flex flex-col gap-2">
            {!cerrada && !detalle.anulada && puedeGestionar && (
              <Link className="ui-primary w-full" to={`${path}/${canonicalId(detalle.id)}/editar`}>
                Editar salida
              </Link>
            )}
            {!cerrada && !detalle.anulada && puedeRegistrar && (
              <Link className="ui-secondary w-full" to={`${path}/${canonicalId(detalle.id)}/peso`}>
                {pesoLabel(detalle.kg_confirmados_destino != null)}
              </Link>
            )}
            {!cerrada && !detalle.anulada && puedeGestionar && (
              <button
                type="button"
                className="ui-secondary min-h-11 text-warning-text"
                onClick={() => {
                  setConfirmandoId(detalle.id);
                  setError(null);
                }}
              >
                Anular
              </button>
            )}
            <button
              type="button"
              className="ui-secondary w-full"
              onClick={() => setDetalleId(null)}
            >
              Cerrar
            </button>
          </div>
        </ModalOverlay>
      )}
      {confirmandoId != null && (
        <ConfirmModal
          title="Anular salida"
          message="¿Anular esta salida? Las aves volverán al saldo disponible."
          confirmLabel={busy ? 'Anulando…' : 'Anular salida'}
          cancelLabel="Cancelar"
          onCancel={() => {
            if (!saving.current) {
              setConfirmandoId(null);
              setError(null);
            }
          }}
          onConfirm={confirmarAnulacion}
          pending={busy}
          error={error}
          danger
        />
      )}
    </section>
  );
}
